<template>
  <div
    class="subprogram-element"
    :class="{ 'subprogram-element--inactive': isInactive }"
  >
    <el-tooltip>
      <div slot="content">
        <p
          class="subprogram-element__pending__option"
          v-for="(pendingInscription, i) in pendingInscriptions"
          :key="`subprogram-element__pending__option--${i}`"
        >
          <i class="icon-player" />
          <span v-text="`${pendingInscription.firstName} ${pendingInscription.lastName}`" />
        </p>
      </div>
      <div
        v-if="hasPendingInscriptions"
        class="subprogram-element__pending"
      >
        <i class="subprogram-element__pending__icon icon-clock" />
        <app-i18n code="entities.inscription.status.pendingApproval" />
        <span
          class="subprogram-element__pending__count"
          v-text="`(${pendingInscriptions.length})`"
        />
      </div>
    </el-tooltip>

    <div class="subprogram-element__wrapper">
      <div class="subprogram-element__header">
        <div
          class="subprogram-element__header__status"
          :class="`subprogram-element__header__status--${subprogram.status}`"
        >
          <i
            class="subprogram-element__header__status__icon"
            :class="getStatusIcon(subprogram.status)"
          />
          <app-i18n :code="getStatusI18nCode(subprogram.status)" />
        </div>
        <span
          class="subprogram-element__header__name"
          v-text="subprogram.category.label"
        />
        <div class="subprogram-element__header__year">
          <span v-text="subprogram.category.minBirthYear" />
          <span v-text="' - '" />
          <span v-text="subprogram.category.maxBirthYear" />
        </div>
        <div class="subprogram-element__header__info">
          <i class="subprogram-element__header__info__icon icon-family" />
          <app-i18n :code="`enumerators.gender.${subprogram.gender}`" />
        </div>
      </div>
      <div class="subprogram-element__content">
        <div class="subprogram-element__content__block subprogram-element__content__block--dates">
          <i class="subprogram-element__content__block__icon icon-calendar" />
          <app-i18n
            class="subprogram-element__content__block__title"
            code="entities.subprogram.fields.duration"
          />
          <div class="subprogram-element__content__block__info">
            <span>{{ subprogram.startDate | parseDateModel }}</span>
            <span
              class="subprogram-element__content__block__info__date-separator"
              v-text="' - '"
            />
            <span>{{ subprogram.endDate | parseDateModel }}</span>
          </div>
        </div>

        <div
          class="subprogram-element__content__block subprogram-element__content__block--dates"
          v-if="subprogram.tryout.exist"
        >
          <i class="subprogram-element__content__block__icon icon-cone" />
          <app-i18n
            class="subprogram-element__content__block__title"
            code="entities.subprogram.fields.tryout.title"
          />
          <div class="subprogram-element__content__block__info">
            <span>{{ subprogram.tryout.startDate | parseDateModel }}</span>
            <span
              class="subprogram-element__content__block__info__date-separator"
              v-text="' - '"
            />
            <span>{{ subprogram.tryout.endDate | parseDateModel }}</span>
          </div>
        </div>

        <div v-if="subprogram.competitions.length > 0" class="subprogram-element__content__block subprogram-element__content__block--list">
          <i class="subprogram-element__content__block__icon icon-cup" />
          <app-i18n
            class="subprogram-element__content__block__title"
            code="entities.subprogram.fields.competitions.title"
          />
          <ul class="subprogram-element__content__block__info">
            <li
              v-for="competition in subprogram.competitions"
              :key="`subprogram-element__content__block__info--competition--${competition}`"
              v-text="competition"
            />
          </ul>
        </div>

        <div class="subprogram-element__content__block subprogram-element__content__block--list">
          <i class="subprogram-element__content__block__icon icon-location" />
          <app-i18n
            class="subprogram-element__content__block__title"
            code="entities.subprogram.fields.locations"
          />
          <ul class="subprogram-element__content__block__info">
            <li
              v-for="location in subprogram.locations"
              :key="`subprogram-element__content__block__info--location--${location.name}`"
            >
              <el-link
                type="primary"
                :href="`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`"
                target="_blank"
              >{{ location.name }}</el-link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'subprogram-element',
  props: {
    subprogram: {
      type: Object,
      default: () => ({}),
    }
  },
  computed: {
    isInactive() {
      return !['published', 'regOpened'].includes(this.subprogram.status)
    },
    pendingInscriptions() {
      return this.subprogram.pendingInscriptions
    },
    hasPendingInscriptions() {
      const { pendingInscriptions: pending } = this
      return !!pending && Array.isArray(pending) && pending.length > 0
    },
  },
  methods: {
    getStatusIcon(status) {
      switch (status) {
        case 'published':
          return 'icon-info'
        case 'regOpened':
          return 'icon-accept'
        case 'regClosed':
          return 'icon-quit'
        default:
          return null
      }
    },
    getStatusI18nCode(status) {
      switch (status) {
        case 'published':
          return 'enumerators.subprogramStatus.comingSoon'
        case 'regOpened':
          return 'enumerators.subprogramStatus.regOpened'
        case 'regClosed':
          return 'enumerators.subprogramStatus.regClosed'
        default:
          return null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.subprogram-element {
  background-color: #ffffff;
  box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);
  margin-top: 40px;
  border-bottom: 3px solid #2acccc;
  cursor: default;

  &--inactive {
    opacity: .5;
  }

  @include mobile {
    margin-top: 24px;
  }

  &__wrapper {
    padding: 20px 24px;
  }

  &__pending {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr auto 1fr;
    grid-gap: 4px;

    width: 100%;
    height: 32px;
    background-color: #2acccc;
    color: #ffffff;
    font-size: 12px;
    font-weight: bold;
    font-style: normal;
    cursor: pointer;

    &__icon {
      justify-self: end;
    }

    &__count {
      font-weight: normal;
    }

    &__option {
      display: grid;
      grid-template-columns: auto 1fr;
      align-items: center;
      margin-bottom: 2px;

      &:last-of-type {
        margin-bottom: 0;
      }

      i {
        font-size: 14px;
        margin-right: 4px;
      }
    }
  }

  &__header {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'name status'
      'year year'
      'info info';
    grid-column-gap: 8px;
    grid-row-gap: 5px;

    padding: 0 0 10px 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);

    @include mobile {
      grid-template-columns: 1fr auto;
      grid-template-areas:
        '. status'
        'name name'
        'year year'
        'info info';
    }

    @include tablet {
      grid-template-columns: 1fr auto;
      grid-template-areas:
        '. status'
        'name name'
        'year year'
        'info info';
    }

    &__status {
      grid-area: status;

      display: grid;
      grid-template-columns: auto 1fr;
      grid-gap: 6px;
      align-items: center;

      font-size: 10px;
      font-weight: normal;
      line-height: 1;
      color: #ffffff;

      width: auto;
      height: 32px;
      padding: 0 8px;
      border-radius: 8px;
      box-shadow: 0 2px 5px 0 rgba(223, 223, 223, 0.7);

      @include mobile {
        margin-bottom: 8px;
      }

      @include tablet {
        margin-bottom: 8px;
      }

      &--published {
        background-color: #ffcd07;
      }

      &--regOpened {
        background-color: #2acc73;
      }

      &--regClosed {
        background-color: #ee7266;
      }

      &__icon {
        font-size: 14px;
      }
    }

    &__name {
      grid-area: name;

      font-size: 40px;
      font-weight: 300;
      letter-spacing: -3px;
      line-height: 1;
      color: #5c5c5c;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__year {
      grid-area: year;

      font-size: 14px;
      font-weight: 300;
      letter-spacing: -0.4px;
      line-height: 1;
      color: rgba(64, 64, 64, 0.35);

      margin: 2px 0 8px;
    }

    &__info {
      grid-area: info;

      display: grid;
      grid-template-columns: auto 1fr;
      grid-gap: 8px;

      font-size: 14px;
      font-weight: 300;
      line-height: normal;
      letter-spacing: normal;
      color: #404040;

      margin: 0 0 8px;
    }
  }

  &__content {
    padding: 24px 0 8px;

    &__block {
      display: grid;
      grid-template-columns: 22px 1fr;
      grid-gap: 10px;
      grid-template-areas:
        'icon title'
        'info info';
      align-items: center;
      margin-bottom: 40px;

      &:last-of-type {
        margin-bottom: 0;
      }

      &--dates {
        .subprogram-element__content__block__info {
          display: grid;
          grid-template-columns: auto 1fr auto 1fr;

          &__date-separator {
            align-self: center;
            justify-self: center;
          }
        }
      }

      &--list {
        .subprogram-element__content__block__info {
          margin: 0;
          padding: 0;
          list-style: none;

          li {
            margin-bottom: 5px;

            .el-link {
              color: #6bc2bc;
              font-weight: 300;
            }

            &:last-of-type {
              margin-bottom: 0;
            }
          }
        }
      }

      &__icon {
        grid-area: icon;

        font-size: 22px;
        color: #999999;
      }

      &__title {
        grid-area: title;

        font-size: 14px;
        font-weight: 600;
        letter-spacing: -0.3px;
        color: #404040;
      }

      &__info {
        grid-area: info;

        font-size: 14px;
        font-weight: 300;
        letter-spacing: -0.3px;
        color: #404040;
      }
    }
  }
}
</style>
